import { CloseOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppPayFrequencySelect from '@nxsys/components/AppPayFrequencySelect';
import { PayFrequencyValue } from '@nxsys/components/AppPayFrequencySelect/type';
import AppSelect from '@nxsys/components/AppSelect';
import AppSelectSync, { OptionType } from '@nxsys/components/AppSelectSync';
import TaxWeekMonthSelect from '@nxsys/components/AppTaxWeekMonthSelect';
import { PSR_SUBMISSION_STATUS } from '@nxsys/constants/psr';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { Form } from 'antd';
import React, { useEffect, useState } from 'react';
import { useEmployerSearchScope } from '@nxsys/hooks/useEmployerSearchScope';
import { useEmployerFieldLabels } from '@nxsys/hooks/useEmployerFieldLabels';

export type PSRFilterValues = {
  payFrequency: PayFrequencyValue | null;
  taxPeriod: number | null;
  status: PSR_SUBMISSION_STATUS | null;
  companyRefs: OptionType[];
};

type PSRFilterModalProps = {
  isOpen: boolean;
  toggle: () => void;
  values: PSRFilterValues;
  onFilter: (values: PSRFilterValues) => void;
  showStatus?: boolean;
};

const DEFAULT_VALUES: PSRFilterValues = {
  payFrequency: null,
  taxPeriod: null,
  status: null,
  companyRefs: [],
};

const PSRFilterModal: React.FC<PSRFilterModalProps> = ({
  isOpen,
  toggle,
  values,
  onFilter,
  showStatus = false,
}) => {
  const { messages } = useTypedIntl();
  const employerLabels = useEmployerFieldLabels();
  const employerSearchScope = useEmployerSearchScope();
  const [draft, setDraft] = useState<PSRFilterValues>(values);

  // Start from the applied filters each time the modal opens, so Cancel discards edits
  useEffect(() => {
    if (isOpen) setDraft(values);
  }, [isOpen]);

  const statusOptions = [
    { label: messages['common.success'], value: PSR_SUBMISSION_STATUS.SUCCESS },
    { label: messages['common.updated'], value: PSR_SUBMISSION_STATUS.UPDATED },
    { label: messages['common.warning'], value: PSR_SUBMISSION_STATUS.WARNING },
    { label: messages['common.failed'], value: PSR_SUBMISSION_STATUS.FAILED },
  ];

  const ableToClearFilter =
    !!draft.payFrequency ||
    !!draft.taxPeriod ||
    !!draft.status ||
    draft.companyRefs.length > 0;

  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDraft(DEFAULT_VALUES);
  };

  const handlePayFrequencyChange = (payFrequency?: PayFrequencyValue) => {
    // Tax period numbering depends on the frequency, so a stale period is dropped
    if ((payFrequency || null) === draft.payFrequency) return;
    setDraft({ ...draft, payFrequency: payFrequency || null, taxPeriod: null });
  };

  const handleApply = () => {
    onFilter(draft);
    toggle();
  };

  return (
    <AppModalContainer
      title={messages['common.filter']}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: messages['common.apply'],
          buttonType: 'primary',
          onClick: handleApply,
        },
      ]}
      closeIcon={
        <div style={{ marginRight: 90 }}>
          <AppButton
            name={messages['common.clearAll']}
            leftIcon={<CloseOutlined />}
            onClick={handleClearAll as any}
            disabled={!ableToClearFilter}
          />
        </div>
      }
      minHeight="200px"
    >
      <Form layout="vertical">
        <Form.Item label={messages['common.payFrequency']}>
          <AppPayFrequencySelect
            placeholder={messages['common.payFrequency']}
            value={draft.payFrequency}
            onFilter={(e) => handlePayFrequencyChange(e.payFrequency)}
            className="!w-full"
            allowClear={true}
          />
        </Form.Item>
        <Form.Item label={messages['common.taxPeriod']}>
          <TaxWeekMonthSelect
            placeholder={messages['common.taxPeriod']}
            value={draft.taxPeriod}
            onFilter={(e) =>
              setDraft({ ...draft, taxPeriod: e.taxPeriod || null })
            }
            className="!w-full"
            paymentFrequency={draft.payFrequency || 'WEEKLY'}
            disabled={!draft.payFrequency}
          />
        </Form.Item>
        {showStatus && (
          <Form.Item label={messages['common.status']}>
            <AppSelect
              placeholder={messages['common.status']}
              options={statusOptions}
              value={statusOptions.find(
                (option) => option.value === draft.status,
              )}
              onChange={(option?: { label: string; value: string }) =>
                setDraft({
                  ...draft,
                  status: (option?.value as PSR_SUBMISSION_STATUS) || null,
                })
              }
              onClear={() => setDraft({ ...draft, status: null })}
              allowClear={true}
              className="!w-full"
            />
          </Form.Item>
        )}
        <Form.Item label={employerLabels.label}>
          <AppSelectSync
            type="company"
            mode="multiple"
            apiType="searchCompany"
            value={draft.companyRefs}
            placeholder={employerLabels.filterPlaceholder}
            paramFilter={employerSearchScope}
            onChangeValue={(value: OptionType | OptionType[] | null) =>
              setDraft({
                ...draft,
                companyRefs: Array.isArray(value) ? value : [],
              })
            }
            className="!w-full"
          />
        </Form.Item>
      </Form>
    </AppModalContainer>
  );
};

export default PSRFilterModal;
